/**
 * @fileoverview User Progress Context - Focused on usage progress and counters
 * Part of Phase 2.3 context optimization - split from monolithic UserContext
 */

import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useStore } from '../../store';
import { useUserLimits } from './hooks';
import { useAddError } from '../ErrorContext';
import { ApiError, ErrorCode } from '../../services/api/errors';
import { UserProgressContext } from './UserProgressContext.shared';
import type { UserProgressContextType } from './UserProgressContext.shared';

interface ProgressIndicator {
    current: number;
    max: number;
    percentage: number;
    isAtLimit: boolean;
    isNearLimit: boolean; // 80% of limit
}

interface UserProgressProviderProps {
    children: ReactNode;
}

export const UserProgressProvider = ({ children }: UserProgressProviderProps) => {
    const store = useStore();
    const { usage, limits } = useUserLimits();
    const addError = useAddError();

    // Memoize strategy progress calculations
    const strategyProgress = useMemo((): ProgressIndicator => {
        const max = limits.maxCustomStrategies === -1 ? Infinity : limits.maxCustomStrategies;
        const current = usage.strategiesCreated;
        const percentage = max === Infinity ? 0 : (current / max) * 100;

        return {
            current,
            max,
            percentage,
            isAtLimit: current >= max,
            isNearLimit: max !== Infinity && percentage >= 80
        };
    }, [usage.strategiesCreated, limits.maxCustomStrategies]);

    // Memoize backtest progress calculations
    const backtestProgress = useMemo((): ProgressIndicator => {
        const max = limits.maxSavedBacktests === -1 ? Infinity : limits.maxSavedBacktests;
        const current = usage.savedBacktests;
        const percentage = max === Infinity ? 0 : (current / max) * 100;

        return {
            current,
            max,
            percentage,
            isAtLimit: current >= max,
            isNearLimit: max !== Infinity && percentage >= 80
        };
    }, [usage.savedBacktests, limits.maxSavedBacktests]);

    // Status helpers
    const hasActiveUsage = useMemo(() =>
        usage.strategiesCreated > 0 || usage.savedBacktests > 0,
        [usage.strategiesCreated, usage.savedBacktests]
    );

    const isApproachingLimits = useMemo(() =>
        strategyProgress.isNearLimit || backtestProgress.isNearLimit,
        [strategyProgress.isNearLimit, backtestProgress.isNearLimit]
    );

    // Action to increment strategy count with error handling
    const incrementStrategyCount = useCallback(() => {
        try {
            store.incrementStrategyCount();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            const apiError = new ApiError(
                ErrorCode.VALIDATION_ERROR,
                'Failed to update strategy count',
                { originalError: errorMessage }
            );
            addError(apiError);
        }
    }, [store, addError]);

    // Action to increment backtest count with error handling
    const incrementBacktestCount = useCallback(() => {
        try {
            store.incrementBacktestCount();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            const apiError = new ApiError(
                ErrorCode.VALIDATION_ERROR,
                'Failed to update backtest count',
                { originalError: errorMessage }
            );
            addError(apiError);
        }
    }, [store, addError]);

    // Memoize context value to prevent unnecessary re-renders with specific dependencies
    const contextValue = useMemo((): UserProgressContextType => ({
        strategyProgress,
        backtestProgress,
        incrementStrategyCount,
        incrementBacktestCount,
        hasActiveUsage,
        isApproachingLimits
    }), [
        strategyProgress.current,
        strategyProgress.max,
        strategyProgress.percentage,
        strategyProgress.isAtLimit,
        strategyProgress.isNearLimit,
        backtestProgress.current,
        backtestProgress.max,
        backtestProgress.percentage,
        backtestProgress.isAtLimit,
        backtestProgress.isNearLimit,
        incrementStrategyCount,
        incrementBacktestCount,
        hasActiveUsage,
        isApproachingLimits
    ]);

    return (
        <UserProgressContext.Provider value={contextValue}>
            {children}
        </UserProgressContext.Provider>
    );
};
